/**
 * Shared metadata field definitions, validation rules, and utilities
 * used by both SingleEstimateMetadataReview and MetadataReviewModal.
 */

// --- Types ---

export interface MetadataField {
    key: string;
    label: string;
    category: string;
    enumOptions?: { value: string; label: string }[];
}

// --- Field Definitions ---

export const METADATA_FIELDS: MetadataField[] = [
    { key: "insuredAddress", label: "Property Address", category: "Property Information" },
    { key: "insuredName", label: "Insured Name", category: "Insured Information" },
    { key: "insuredPhone", label: "Insured Phone", category: "Insured Information" },
    { key: "insuredEmail", label: "Insured Email", category: "Insured Information" },
    { key: "claimNumber", label: "Claim Number", category: "Claim Information" },
    { key: "policyNumber", label: "Policy Number", category: "Claim Information" },
    { key: "estimatorCompany", label: "Insurance Company", category: "Claim Information" },
    { key: "dateOfLoss", label: "Date of Loss", category: "Claim Information" },
    { key: "typeOfLoss", label: "Type of Loss", category: "Claim Information" },
];

export const REQUIRED_FIELDS = new Set(["insuredAddress", "insuredName", "claimNumber"]);

export const VALIDATION_RULES: Record<string, (value: string) => string | null> = {
    insuredAddress: (value) => value.trim().length < 5 ? "Address must be at least 5 characters" : null,
    insuredName: (value) => !value.trim() ? "Name cannot be empty" : null,
    claimNumber: (value) => !value.trim() ? "Claim Number cannot be empty" : null,
    typeOfLoss: (value) => {
        if (value && value.trim().length > 100) return "Loss type must be 100 characters or less";
        return null;
    },
};

// --- Pre-computed category grouping ---

export const FIELDS_BY_CATEGORY: Record<string, MetadataField[]> = METADATA_FIELDS.reduce(
    (acc, field) => {
        if (!acc[field.category]) acc[field.category] = [];
        acc[field.category].push(field);
        return acc;
    },
    {} as Record<string, MetadataField[]>,
);

// --- Helpers ---

export const getValue = (obj: any, key: string): any => obj?.[key];

export const formatValue = (
    value: any,
    enumOptions?: { value: string; label: string }[],
): string => {
    if (!value) return "Not available";
    if (enumOptions) {
        const option = enumOptions.find((o) => o.value === value);
        return option ? option.label : String(value);
    }
    if (typeof value === "string") return value;
    if (value instanceof Date) return value.toLocaleDateString();
    return String(value);
};

// --- Validation ---

/**
 * Validate a resolved value for a given metadata field key.
 * Returns an error message string, or null if valid.
 */
export function validateField(key: string, value: string | null | undefined): string | null {
    const rule = VALIDATION_RULES[key];
    if (rule) {
        return rule(String(value || ""));
    }
    if (REQUIRED_FIELDS.has(key) && (!value || !String(value).trim())) {
        return "This field is required";
    }
    return null;
}
